import type { Expression, FillPaint } from 'mapbox-gl';
import {
  colormapStops,
  sampleRamp,
  type ColorMapping,
  type ColormapId,
  type RampStop,
} from 'components/colormaps';
import { roundRangeOutward } from '../filter-modal/value-histogram';
import { defaultRampStops } from './utils';
import type { SubsurfaceMapRow } from './types';

/**
 * The one colour rule for every subsurface map (v3 mock: "Applies to all N
 * subsurface maps"). Null in state means no rule: each map paints in its own
 * default ramp over its own range.
 */
export interface SubsurfaceColorRule {
  mapping: ColorMapping;
  rampId: ColormapId;
  reversed: boolean;
  /** Stops set in the ramp editor; when present they replace the named ramp. */
  customStops: RampStop[] | null;
  /** True: each map spans its own min/max. False: every map spans min..max below. */
  perSurfaceRange: boolean;
  rangeMin: number;
  rangeMax: number;
  binCount: number;
}

/** The Bins stepper's bounds. */
export const MIN_BIN_COUNT = 2;
export const MAX_BIN_COUNT = 12;

const DEFAULT_BIN_COUNT = 6;

/**
 * The mock's default rule: Viridis, gradient, each surface over its own range,
 * with the shared range preset to the pooled min/max.
 * @param {[number, number]} range The pooled [min, max] for a shared range.
 * @return {SubsurfaceColorRule} The rule.
 */
export function newColorRule(range: [number, number]): SubsurfaceColorRule {
  const [rangeMin, rangeMax] = roundRangeOutward(range);
  return {
    mapping: 'gradient',
    rampId: 'viridis',
    reversed: false,
    customStops: null,
    perSurfaceRange: true,
    rangeMin,
    rangeMax,
    binCount: DEFAULT_BIN_COUNT,
  };
}

/**
 * Whether a rule holds nothing but custom stops: every other setting is still the
 * one newColorRule starts from (the range doesn't count while it is per-surface).
 * @param {SubsurfaceColorRule} rule The rule.
 * @return {boolean} True when dropping its custom stops leaves a default rule.
 */
export function onlyCustomStops(rule: SubsurfaceColorRule): boolean {
  const fresh = newColorRule([rule.rangeMin, rule.rangeMax]);
  return (
    rule.perSurfaceRange &&
    rule.mapping === fresh.mapping &&
    rule.rampId === fresh.rampId &&
    rule.reversed === fresh.reversed &&
    rule.binCount === fresh.binCount
  );
}

/**
 * The stops a rule paints with: its custom stops, or its named ramp.
 * @param {SubsurfaceColorRule} rule The rule.
 * @return {RampStop[]} Stops from low to high.
 */
export function ruleStops(rule: SubsurfaceColorRule): RampStop[] {
  return rule.customStops ?? colormapStops(rule.rampId, rule.reversed);
}

/** The painted range: literal when known, else read off each cell's tile feature. */
type RangeBound = number | Expression;

/** A cell's value, as the cells tile carries it. */
const VALUE: Expression = ['get', 'value'];

// Cells outside the value filter dim to this fraction of the layer's opacity rather
// than vanish, so the filter answers "where" while the rest stays legible (PRD).
export const DIMMED_ALPHA = 64 / 255;

/**
 * The range the shown map is painted across: the rule's global range, or the
 * map's own. A map whose stats aren't built yet reads its grid's range off the
 * tile features (`min_val`/`max_val`), so the paint never waits for stats.
 * @param {SubsurfaceMapRow} map The shown map.
 * @param {SubsurfaceColorRule | null} rule The colour rule, if any.
 * @return {[RangeBound, RangeBound]} [min, max].
 */
function paintRange(
  map: SubsurfaceMapRow,
  rule: SubsurfaceColorRule | null
): [RangeBound, RangeBound] {
  if (rule && !rule.perSurfaceRange) return [rule.rangeMin, rule.rangeMax];
  if (map.valueMin !== null && map.valueMax !== null) {
    return [map.valueMin, map.valueMax];
  }
  return [
    ['get', 'min_val'],
    ['get', 'max_val'],
  ];
}

/**
 * Where a cell's value sits in the range, 0 at min and 1 at max (clamped by the
 * ramp expressions it feeds).
 * @param {RangeBound} min The range's low end.
 * @param {RangeBound} max The range's high end.
 * @return {Expression} The 0–1 position.
 */
const position = (min: RangeBound, max: RangeBound): Expression => [
  '/',
  ['-', VALUE, min],
  ['-', max, min],
];

/**
 * How far below a shared position an earlier stop is moved: Mapbox needs ascending
 * inputs, and this keeps the hard edge the legend draws at that position.
 */
const STOP_NUDGE = 1e-6;

/**
 * A gradient over the stops: Mapbox interpolates the colour between them, so
 * every stop paints exactly its colour at its position. Stops sharing a position
 * (two handles dragged together) all stay: each earlier one is nudged just below
 * the shared position, so the map changes colour there as sharply as the legend.
 * @param {RampStop[]} stops The ramp's stops.
 * @param {Expression} at The 0–1 position.
 * @return {Expression} The colour.
 */
function gradient(stops: RampStop[], at: Expression): Expression {
  const ordered = [...stops].sort((a, b) => a.offset - b.offset);
  if (ordered.length === 1) return ['literal', ordered[0].color] as Expression;
  const positions = ordered.map((stop) => stop.offset / 100);
  for (let index = positions.length - 2; index >= 0; index -= 1) {
    if (positions[index] >= positions[index + 1]) {
      positions[index] = positions[index + 1] - STOP_NUDGE;
    }
  }
  return [
    'interpolate',
    ['linear'],
    at,
    ...ordered.flatMap((stop, index) => [positions[index], stop.color]),
  ];
}

/**
 * Equal bands over the range, each painted with the ramp colour at its centre
 * (the same colours colorBins lists). A cell on a band edge is in the upper band,
 * like the legend's bounds say.
 * @param {RampStop[]} stops The ramp's stops.
 * @param {number} binCount Number of bands.
 * @param {Expression} at The 0–1 position.
 * @return {Expression} The colour.
 */
function banded(
  stops: RampStop[],
  binCount: number,
  at: Expression
): Expression {
  // A step needs at least one stop, so never fewer than the Bins field allows.
  const bins = Math.max(MIN_BIN_COUNT, Math.round(binCount));
  const colour = (index: number) =>
    sampleRamp(stops, ((index + 0.5) / bins) * 100);
  const steps = Array.from({ length: bins - 1 }, (_, index) => [
    (index + 1) / bins,
    colour(index + 1),
  ]).flat();
  return ['step', at, colour(0), ...steps];
}

/**
 * The cells layer's paint for the shown map: with no rule, its own default ramp
 * over its own range; with a rule, the rule's stops, mapping and range. A flat
 * range (min = max) paints the ramp's middle colour. The value filter dims cells
 * outside it; the layer's transparency scales everything.
 * @param {SubsurfaceMapRow} map The shown map.
 * @param {SubsurfaceColorRule | null} rule The colour rule, if any.
 * @param {{ low: number, high: number } | null} mask The applied value filter.
 * @param {number} opacityPercent The layer opacity, 0–100.
 * @return {FillPaint} The fill paint.
 */
export function cellsPaint(
  map: SubsurfaceMapRow,
  rule: SubsurfaceColorRule | null,
  mask: { low: number; high: number } | null,
  opacityPercent: number
): FillPaint {
  const stops = rule ? ruleStops(rule) : defaultRampStops(map.defaultColorRamp);
  const [min, max] = paintRange(map, rule);
  const at = position(min, max);
  const ramp =
    rule?.mapping === 'binned'
      ? banded(stops, rule.binCount, at)
      : gradient(stops, at);
  const flat = sampleRamp(stops, 50);
  return {
    'fill-color': ['case', ['==', max, min], flat, ramp] as Expression,
    'fill-opacity': cellsOpacity(opacityPercent / 100, mask),
    'fill-antialias': false,
  };
}

/**
 * The layer opacity, dimmed for cells outside the value filter. Cell values are
 * float32, so the bounds are compared at that precision (a cell on a typed bound
 * such as 1500.1 stays in); an open-ended bound adds no test.
 * @param {number} opacity The layer opacity, 0–1.
 * @param {{ low: number, high: number } | null} mask The applied value filter.
 * @return {number | Expression} The opacity, or a per-cell expression.
 */
function cellsOpacity(
  opacity: number,
  mask: { low: number; high: number } | null
): number | Expression {
  const tests: Expression[] = [];
  if (mask && Number.isFinite(mask.low)) {
    tests.push(['>=', VALUE, Math.fround(mask.low)]);
  }
  if (mask && Number.isFinite(mask.high)) {
    tests.push(['<=', VALUE, Math.fround(mask.high)]);
  }
  if (!tests.length) return opacity;
  return ['case', ['all', ...tests], opacity, opacity * DIMMED_ALPHA];
}
